import { useEffect, useMemo, useRef, useState } from "react";

import BackOrExploreButton from "@/components/buttons/back-or-explore-button";
import CloudinaryImage, {
  GAME_FALLBACK_IMAGE_ID,
} from "@/components/cloudinary-image";
import GalleryThumbnailsModal from "@/components/gallery-thumbnails-modal";
import { ShareIcon } from "@/components/icons/share";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import useHistory from "@/hooks/use-history";
import useShare from "@/hooks/use-share";
import { cn } from "@/lib/utils";

import useMetaProps from "../hooks/use-meta-props";
import { type GameType, ImageTypeEnum } from "../types";
import { AllPhotosIcon, NextIcon, PrevIcon } from "./icons";

const NAV_BUTTON_CLASS_NAME = cn(
  "absolute top-1/2 z-2 size-6.5 rounded-full bg-white md:size-10 md:-translate-y-1/2"
);

interface CoverImagesProps {
  className?: string;
  game: GameType;
}

export default function CoverImages({ className, game }: CoverImagesProps) {
  const [desktopImages, setDesktopImages] = useState<string[]>([]);
  const [desktopIndex, setDesktopIndex] = useState(0);
  const [mobileIndex, setMobileIndex] = useState(0);
  const [isAllPhotosOpen, setIsAllPhotosOpen] = useState(false);
  const imageContainerRef = useRef<HTMLDivElement>(null);

  const { lastUrl } = useHistory();
  const canGoBack =
    !!lastUrl &&
    !lastUrl.startsWith("/experience/") &&
    !lastUrl.startsWith("/cart/");
  const { description, title, url } = useMetaProps(game);
  const { onShare } = useShare({ text: description, title, url });

  const mobileImages = useMemo(() => {
    const _mobileImages = (game.gameimageSet || []).map(
      ({ imageId }) => imageId
    );
    setMobileIndex(0);
    return _mobileImages.length ? _mobileImages : [GAME_FALLBACK_IMAGE_ID];
  }, [game.gameimageSet]);

  const rawDesktopImages = useMemo(() => {
    const _desktopImages = (game.gameimageSet || [])
      .filter(({ imageType }) => imageType === ImageTypeEnum.IMAGE)
      .map(({ imageId }) => imageId);
    setDesktopImages(_desktopImages);
    setDesktopIndex(0);
    return _desktopImages.length ? _desktopImages : [GAME_FALLBACK_IMAGE_ID];
  }, [game.gameimageSet]);

  const setImageByIndex = (index: number) => {
    setDesktopIndex(index);

    const rawCopy = [...rawDesktopImages];
    const lastImages = rawCopy.slice(0, index);
    const firstImages = rawCopy.slice(index);
    setDesktopImages([...firstImages, ...lastImages]);
  };

  const handleImage = (index: number) => () => setImageByIndex(index);

  const handleNextImage = () => {
    const newIndex = desktopIndex + 1;

    setImageByIndex(newIndex >= desktopImages.length ? 0 : newIndex);
  };

  const handlePrevImage = () => {
    const newIndex = desktopIndex - 1;
    setImageByIndex(newIndex < 0 ? desktopImages.length - 1 : newIndex);
  };

  useEffect(() => {
    const { current } = imageContainerRef;

    if (!current) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const activated = entries.reduce((max, entry) =>
          entry.intersectionRatio > max.intersectionRatio ? entry : max
        );

        if (activated.intersectionRatio > 0) {
          setMobileIndex(
            Number.parseInt(activated.target.getAttribute("id") || "0", 10)
          );
        }
      },
      {
        root: current,
        threshold: 0.5,
      }
    );

    for (let i = 0; i < current.children.length; i++) {
      current.children[i].setAttribute("id", i.toString());
      observer.observe(current.children[i]);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div
      className={cn(
        "relative inset-x-0 top-0 overflow-hidden md:absolute",
        'before:absolute before:inset-0 before:z-1 before:hidden before:bg-linear-[180deg,#000000B3_9%,#00000080_38%,#000000BF_62%,#000000CC_80%] before:content-[""] before:md:block',
        className
      )}
    >
      {/** biome-ignore lint/a11y/noStaticElementInteractions: its ok */}
      {/** biome-ignore lint/a11y/useKeyWithClickEvents: its ok */}
      {/** biome-ignore lint/a11y/noNoninteractiveElementInteractions: legacy */}
      <div
        className={cn(
          "scrollbar-hidden flex aspect-375/230 snap-x snap-mandatory overflow-auto md:hidden",
          'before:pointer-events-none before:absolute before:inset-0 before:z-1 before:block before:bg-black/15 before:content-[""]'
        )}
        onClick={() => setIsAllPhotosOpen(true)}
        ref={imageContainerRef}
      >
        {mobileImages.map((imageId, index) => (
          <CloudinaryImage
            alt={game.name}
            className="size-full shrink-0 snap-center"
            imageId={imageId}
            key={imageId}
            loading={index ? "lazy" : "eager"}
            rW={964}
          />
        ))}
      </div>
      <div
        className={cn(
          "relative hidden h-127 overflow-hidden md:flex",
          'before:absolute before:inset-0 before:z-1 before:block before:bg-black/20 before:content-[""]'
        )}
      >
        {desktopImages.map((imageId, index) => (
          <CloudinaryImage
            alt={game.name}
            className="size-full w-1/3 shrink-0 grow"
            imageId={imageId}
            key={imageId}
            loading={index ? "lazy" : "eager"}
            rW={964}
          />
        ))}
      </div>
      <div className="absolute inset-x-0 top-0 z-2 flex items-center justify-between px-6 pt-6 md:hidden">
        <BackOrExploreButton canGoBack={canGoBack} />
        <Button
          aria-label="Share"
          className="flex size-7.5 items-center"
          onClick={onShare}
        >
          <ShareIcon />
        </Button>
      </div>
      {mobileImages.length > 1 && (
        <Badge className="absolute right-6 bottom-5.5 rounded-full border-0 bg-white/20 px-2.5 py-1 font-normal text-12/[1.2em] text-white -tracking-[0.05em] md:hidden">
          {mobileIndex + 1}/{mobileImages.length}
        </Badge>
      )}
      <div className="absolute inset-x-6 inset-y-0 hidden md:block">
        <div className="relative mx-auto h-full max-w-347.5 px-20">
          {desktopImages.length > 1 && (
            <>
              <Button
                aria-label="Previous"
                className={cn(NAV_BUTTON_CLASS_NAME, "left-0")}
                onClick={handlePrevImage}
              >
                <PrevIcon />
              </Button>
              <Button
                aria-label="Next"
                className={cn(NAV_BUTTON_CLASS_NAME, "right-0")}
                onClick={handleNextImage}
              >
                <NextIcon />
              </Button>
            </>
          )}
          <Button
            className="absolute right-0 bottom-8 z-2 h-9.5 cursor-pointer gap-2.5 rounded-[19px] border border-rose-red bg-white/5 px-3.5 font-medium text-14 text-white hover:bg-white/10"
            onClick={() => setIsAllPhotosOpen(true)}
          >
            <AllPhotosIcon />
            Show all photos
          </Button>
        </div>
        {desktopImages.length > 1 && (
          <div className="pointer-events-none absolute inset-x-0 bottom-4 z-2 flex flex-wrap justify-center gap-2 py-1 md:bottom-6">
            {desktopImages.map((id, index) => (
              <Button
                className={cn(
                  "pointer-events-auto size-2 rounded-full bg-white/40 shadow-dark-magenta/64 transition-[colors,shadow]",
                  index === desktopIndex && "bg-rose-red shadow-0-3-5"
                )}
                key={id}
                onClick={handleImage(index)}
              />
            ))}
          </div>
        )}
      </div>
      <GalleryThumbnailsModal
        onOpenChange={setIsAllPhotosOpen}
        open={isAllPhotosOpen}
        photos={(game.gameimageSet || []).map(({ imageId }) => ({ imageId }))}
      />
    </div>
  );
}
